import { AvatarSizeProps } from 'components/avatar'
import { Button, Flex, Text, Image, ColorProps } from 'components/elements'
import { Dispatch, ReactNode, SetStateAction } from 'react'
import { CSS } from 'theme'
import {
  ChildrenCSS,
  ImageBgOverlayCss,
  ModalBody,
  ModalCss,
  ModalFooter,
  ModalHeader,
} from './modal'
import { UseMutateFunction } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { SvgCircleWrap } from 'components/svg'
import { GroupIconCss } from 'theme/globalStyleCss'

export type MutateFunctionNumber = UseMutateFunction<
  AxiosResponse<any, any>,
  unknown,
  number,
  unknown
>

interface ToggleProps {
  modalSize?: AvatarSizeProps
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  onAccept: () => void
  css?: CSS
  bodyCss?: CSS
  color: ColorProps
  title?: {
    icon?: ReactNode
    text: string
    description?: string
  }
  button?:
    | {
        cancelText?: string
        acceptText?: string
        onOptionalCancel?: () => void
      }
    | undefined
  children?: ReactNode
}

export interface ConfirmItem {
  id: number
  show: boolean
  level?: number
  func?: MutateFunctionNumber
}

export const confirmItemData = {
  id: 0,
  show: false,
  level: 0,
  func: () => {},
}

export const ConfirmModal = ({
  modalSize,
  open,
  setOpen,
  css,
  color,
  bodyCss,
  children,
  button,
  title,
  onAccept,
}: ToggleProps) => {
  const { acceptText, cancelText, onOptionalCancel } = button || {}
  const { icon, text = '', description } = title || {}
  // const { id, func } = open || {}

  const handleClose = () => {
    setOpen(false)
  }

  const renderModalHeader = () => (
    <ModalHeader align="start" css={{ border: 'none' }}>
      <SvgCircleWrap
        color={color}
        css={{
          ...GroupIconCss,
          ml: '-$space$4',
          my: '-$space$4',
          mr: '$space$8',
        }}
      >
        {icon}
      </SvgCircleWrap>
      <Image
        className="backgroundImage"
        size="auto"
        url={'/assets/icons/backgroundRing.png'}
        css={ImageBgOverlayCss}
      />
      <Flex direction={'column'}>
        <Text as="h4" css={{ color: '$heading' }}>
          {text}
        </Text>
        {description && (
          <Text as="p" css={{ color: '$heading' }}>
            {description}
          </Text>
        )}
      </Flex>
    </ModalHeader>
  )

  const renderModalBody = () => <ModalBody css={bodyCss}>{children}</ModalBody>

  const renderModalFooter = () => (
    <ModalFooter justify="end" gap="12" css={{ pt: 0, border: 'none' }}>
      {cancelText && (
        <Button
          type={'button'}
          onClick={!!onOptionalCancel ? onOptionalCancel : handleClose}
          color="error"
        >
          {cancelText || 'Decline'}
        </Button>
      )}
      {acceptText && (
        <Button color="primary" type="button" onClick={onAccept}>
          {acceptText || 'Accept'}
        </Button>
      )}
    </ModalFooter>
  )

  return (
    <ModalCss
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <ChildrenCSS css={{ ...css }} size={modalSize}>
        {renderModalHeader()}
        {children && renderModalBody()}
        {renderModalFooter()}
      </ChildrenCSS>
    </ModalCss>
  )
}
